/**
 * StepExecutionPanel.tsx
 *
 * Column 3 content for a protocol step. Previously a second drawer stacked on
 * top of the group drawer; it now renders inline so the group context, the step
 * list and the step itself stay on screen together.
 *
 * The parent mounts this with `key={step.id}` so per-step state (timer, field
 * values, notes) resets when the technician moves to another step.
 */

import React, { useEffect, useRef, useState } from "react";
import { Button, Input, Modal, Progress, Space, Typography } from "antd";
import {
	CheckCircleOutlined,
	ExclamationCircleOutlined,
	PlayCircleOutlined,
	SaveOutlined,
} from "@ant-design/icons";
import { getSessionUser } from "../../../../../../utils/auth/sessionUser";

import { StatusTag } from "../../../../../../components/shared";
import {
	completeGroupStep,
	fetchGroupActionLogs,
	saveGroupStepDraft,
	startGroupStep,
} from "../../../../../../api/labiq/GroupProcessingApi";
import type {
	GroupStepActionLog,
	GroupStepInstance,
	ProcessingGroup,
	ProtocolStep,
	StepActionLog,
} from "../../../../../../types/lab/ProtocolTypes";
import { useNotifications } from "../../../../../../context/NotificationContext";

import RegularStepView from "../stepExecution/RegularStepView";
import ResultProcessingView from "../stepExecution/ResultProcessingView";
import PanelHeader from "./PanelHeader";
import { formatDuration, stepTypeCfg } from "./stepMeta";
import { requireLabiqUserId } from "../../../../../../utils/auth/labiqUserId";

const { Text } = Typography;
const { TextArea } = Input;
const { confirm } = Modal;

interface StepExecutionPanelProps {
	group: ProcessingGroup;
	step: ProtocolStep;
	stepData?: GroupStepInstance;
	onRefresh: (groupData?: ProcessingGroup, skipParentRefresh?: boolean) => void;
	/** Fired once a step is completed so the parent can advance the rail. */
	onStepCompleted?: () => void;
}

/** Seconds → "1:04:07" / "4:07". */
function formatElapsed(seconds: number): string {
	const hours = Math.floor(seconds / 3600);
	const minutes = Math.floor((seconds % 3600) / 60);
	const secs = seconds % 60;
	if (hours > 0) {
		return `${hours}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
	}
	return `${minutes}:${secs.toString().padStart(2, "0")}`;
}

const StepExecutionPanel: React.FC<StepExecutionPanelProps> = ({
	group,
	step,
	stepData,
	onRefresh,
	onStepCompleted,
}) => {
	const user = getSessionUser();
	const isResultStep = step.step_type === "results";
	const isAnalysisProcessing = !!group.analysis_instances && group.analysis_instances.length > 0;
	const processingTypeLabel = isAnalysisProcessing ? "analyses" : "samples";
	const singularLabel = isAnalysisProcessing ? "analysis" : "sample";

	const [fieldValues, setFieldValues] = useState<Record<string, any>>({});
	const [notes, setNotes] = useState("");
	/**
	 * Per-instance field values entered in the RegularStepView table grid.
	 * Key: instance ID (string). Value: field_values map for that instance.
	 */
	const [perSampleFieldValues, setPerSampleFieldValues] = useState<Record<string, Record<string, any>>>({});
	const [savingDraft, setSavingDraft] = useState(false);
	const [actionLogs, setActionLogs] = useState<StepActionLog[] | GroupStepActionLog[]>([]);
	const [timeElapsed, setTimeElapsed] = useState(0);
	const [stepStatus, setStepStatus] = useState(stepData?.status || "pending");
	const [processing, setProcessing] = useState(false);
	const readOnly = stepStatus === "completed" || group.status !== "active";

	const [startTime, setStartTime] = useState<Date | null>(
		stepData?.started_at ? new Date(stepData.started_at) : null,
	);
	const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

	const { showSuccess, showError, showWarning } = useNotifications();

	useEffect(() => {
		if (step.configuration?.required_fields) {
			const initialValues: Record<string, any> = {};
			step.configuration.required_fields.forEach((field) => {
				initialValues[field.field_name] =
					stepData?.field_values?.[field.field_name] || field.default_value || "";
			});
			setFieldValues(initialValues);
		}

		// Restore per-sample field values from a previous draft save
		if (stepData?.per_sample_field_values) {
			setPerSampleFieldValues(stepData.per_sample_field_values);
		}

		if (stepData?.id) {
			loadActionLogs();
		}

		setNotes(stepData?.notes || "");

		// A held or failed group's in-progress step must not tick towards handleTimeout.
		if (stepStatus === "in_progress" && startTime && !readOnly) {
			startTimer();
		}

		return () => {
			if (timerRef.current) {
				clearInterval(timerRef.current);
			}
		};
	}, [step, stepData, stepStatus, startTime, readOnly]);

	const loadActionLogs = async () => {
		if (!stepData?.id) return;

		try {
			const logs = await fetchGroupActionLogs(group.id);
			const stepLogs = logs.filter(
				(log) => "group_step_instance_id" in log && log.group_step_instance_id === stepData.id,
			);
			setActionLogs(stepLogs as GroupStepActionLog[]);
		} catch (error) {
			console.warn("Could not load action logs:", error);
		}
	};

	const startTimer = () => {
		timerRef.current = setInterval(() => {
			if (startTime) {
				const now = new Date();
				const elapsed = Math.floor((now.getTime() - startTime.getTime()) / 1000);
				setTimeElapsed(elapsed);

				if (step.timeout_minutes) {
					const timeoutSeconds = step.timeout_minutes * 60;
					if (elapsed >= timeoutSeconds && stepStatus === "in_progress") {
						handleTimeout();
					}
				}
			}
		}, 1000);
	};

	const handleStartStep = async () => {
		setProcessing(true);
		try {
			let newGroup: ProcessingGroup | undefined = undefined;
			({ group: newGroup } = await startGroupStep(group.id, {
				step_number: step.step_number,
				performed_by: requireLabiqUserId(user),
			}));

			setStepStatus("in_progress");
			setStartTime(new Date());
			startTimer();
			showSuccess(`Step ${step.step_number} started for the ${singularLabel} group`);

			onRefresh(newGroup, true);
		} catch (error) {
			showError("Failed to start step");
			console.error("Step start error:", error);
		} finally {
			setProcessing(false);
		}
	};

	const handleCompleteStep = async () => {
		if (!isResultStep) {
			const requiredFields = step.configuration?.required_fields?.filter((f) => f.required) || [];
			const hasPerSampleValues = Object.keys(perSampleFieldValues).length > 0;

			if (!hasPerSampleValues) {
				// Validate group-level required fields
				const missingFields = requiredFields.filter(
					(field) => !fieldValues[field.field_name] || fieldValues[field.field_name] === "",
				);
				if (missingFields.length > 0) {
					showError(
						`Fill in the required fields before completing: ${missingFields
							.map((f) => f.field_name.replace(/_/g, " "))
							.join(", ")}`,
					);
					return;
				}
			}
		}

		confirm({
			title: "Complete step",
			icon: <ExclamationCircleOutlined />,
			content: `This completes step ${step.step_number} for all ${
				group.sample_count || 0
			} ${processingTypeLabel} in the group and cannot be undone.`,
			okText: "Complete step",
			cancelText: "Cancel",
			onOk: async () => {
				setProcessing(true);
				try {
					const newGroup = await completeGroupStep(group.id, {
						step_id: step.id,
						field_values: fieldValues,
						per_sample_field_values:
							Object.keys(perSampleFieldValues).length > 0 ? perSampleFieldValues : undefined,
						performed_by: requireLabiqUserId(user),
						notes: notes.trim() || undefined,
						applicable_instance_ids: stepData?.applicable_instance_ids ?? undefined,
					});

					setStepStatus("completed");
					if (timerRef.current) {
						clearInterval(timerRef.current);
					}

					showSuccess(`Step ${step.step_number} completed`);
					onRefresh(newGroup, true);
					onStepCompleted?.();
				} catch (error) {
					showError("Failed to complete step");
					console.error("Step completion error:", error);
				} finally {
					setProcessing(false);
				}
			},
		});
	};

	const handleSaveDraft = async () => {
		if (savingDraft) return;
		setSavingDraft(true);
		try {
			await saveGroupStepDraft(group.id, {
				step_id: step.id,
				field_values: fieldValues,
				per_sample_field_values:
					Object.keys(perSampleFieldValues).length > 0 ? perSampleFieldValues : undefined,
				performed_by: requireLabiqUserId(user),
				notes: notes.trim() || undefined,
			});
			showSuccess("Draft saved");
		} catch (error) {
			showError("Failed to save draft");
		} finally {
			setSavingDraft(false);
		}
	};

	const handleTimeout = async () => {
		if (!stepData?.id) return;

		try {
			setStepStatus("timed_out");
			showWarning(`Step ${step.step_number} timed out for the group`);

			if (step.timeout_actions && step.timeout_actions.length > 0) {
				showWarning(`${step.timeout_actions.length} timeout actions will run`);
			}

			onRefresh();
		} catch (error) {
			showError("Failed to handle step timeout");
		}
	};

	const updateFieldValue = (fieldName: string, value: any) => {
		setFieldValues((prev) => ({ ...prev, [fieldName]: value }));
	};

	const timeoutProgress = (): number => {
		if (!step.timeout_minutes || !startTime) return 0;
		return Math.min((timeElapsed / (step.timeout_minutes * 60)) * 100, 100);
	};

	const { label: typeLabel } = stepTypeCfg(step.step_type);
	const perInstanceEstimate = formatDuration(step.duration_estimate ?? 0);
	const groupEstimate = formatDuration((step.duration_estimate ?? 0) * (group.sample_count || 0));

	// ── Header meta line — status, timings, the whole picture on one row ──
	const metaLine = (
		<Space size={6} wrap style={{ rowGap: 2 }}>
			<StatusTag status={stepStatus} size="small" />
			{stepStatus === "in_progress" && !readOnly && (
				<Text style={{ fontSize: 11, fontVariantNumeric: "tabular-nums" }}>
					{formatElapsed(timeElapsed)} elapsed
				</Text>
			)}
			<Text type="secondary" style={{ fontSize: 11 }}>
				· est {perInstanceEstimate} per {singularLabel} · {groupEstimate} for{" "}
				{group.sample_count || 0} {processingTypeLabel}
			</Text>
			{step.timeout_minutes && (
				<Text type="secondary" style={{ fontSize: 11 }}>
					· {step.timeout_minutes}m timeout
				</Text>
			)}
		</Space>
	);

	const actions = readOnly ? null : (
		<Space size={8}>
			{stepStatus === "pending" && (
				<Button
					type="primary"
					size="small"
					icon={<PlayCircleOutlined />}
					onClick={handleStartStep}
					loading={processing}
				>
					Start step
				</Button>
			)}
			{stepStatus === "in_progress" && (
				<>
					<Button size="small" icon={<SaveOutlined />} onClick={handleSaveDraft} loading={savingDraft}>
						Save draft
					</Button>
					<Button
						type="primary"
						size="small"
						icon={<CheckCircleOutlined />}
						onClick={handleCompleteStep}
						loading={processing}
					>
						Complete step
					</Button>
				</>
			)}
		</Space>
	);

	return (
		<div style={{ display: "flex", flexDirection: "column", height: "100%", minWidth: 0 }}>
			<PanelHeader
				eyebrow={`Step ${step.step_number} · ${typeLabel}`}
				title={step.name}
				meta={metaLine}
				actions={actions}
			/>

			{/* Timeout gauge — a small dial next to its own reading, not a page-wide
			    progress bar competing with the step content below it. */}
			{step.timeout_minutes && stepStatus === "in_progress" && !readOnly && (
				<div
					style={{
						flexShrink: 0,
						padding: "5px 16px",
						borderBottom: "1px solid var(--mg-border)",
						display: "flex",
						alignItems: "center",
						gap: 8,
					}}
				>
					<Progress
						percent={timeoutProgress()}
						showInfo={false}
						strokeWidth={4}
						strokeColor={timeoutProgress() > 80 ? "var(--mg-warning)" : "var(--mg-primary)"}
						style={{ width: 160, margin: 0, lineHeight: 1 }}
					/>
					<Text
						type={timeoutProgress() > 80 ? "warning" : "secondary"}
						style={{ fontSize: 11, fontVariantNumeric: "tabular-nums", flexShrink: 0 }}
					>
						{Math.max(0, Math.floor((step.timeout_minutes * 60 - timeElapsed) / 60))}m before timeout
					</Text>
				</div>
			)}

			<div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
				{isResultStep ? (
					<ResultProcessingView group={group} step={step} stepData={stepData} stepStatus={stepStatus} onRefresh={onRefresh} readOnly={readOnly} />
				) : (
					<RegularStepView
						group={group}
						step={step}
						stepData={stepData}
						fieldValues={fieldValues}
						onFieldValueChange={updateFieldValue}
						perSampleFieldValues={perSampleFieldValues}
						onPerSampleFieldValuesChange={setPerSampleFieldValues}
						actionLogs={actionLogs}
						readOnly={readOnly}
					/>
				)}
			</div>

			{!isResultStep && (
				<div
					style={{
						flexShrink: 0,
						padding: "8px 16px 10px",
						borderTop: "1px solid var(--mg-border)",
						background: "var(--mg-bg-elevated)",
					}}
				>
					<div style={{ fontSize: 10, color: "var(--mg-text-secondary)", marginBottom: 4 }}>
						Notes — applied to all {processingTypeLabel} in the group
					</div>
					<TextArea
						value={notes}
						onChange={(e) => setNotes(e.target.value)}
						placeholder="Observations or issues encountered during this step"
						rows={2}
						disabled={readOnly}
						size="small"
					/>
				</div>
			)}
		</div>
	);
};

export default StepExecutionPanel;
